import { useMemo, useState, type ReactNode } from "react"
import type { PaginationState } from "@tanstack/react-table"
import type {
  AdCommentSummary,
  AdReaction,
  AdReactionsReport,
  CommentPlatform,
} from "~/ui-rtk/api/commentModerationApi"
import TableVisualization from "~/ui-rtk/components/ui/charts/TableVisualization"
import { DRAWER_TYPE, type TDrawerConfig } from "~/ui-rtk/constants/drawer"
import type {
  TMetric,
  TableVisualizationColumn,
} from "~/ui-rtk/constants/table-visualization-types"
import useDrawer from "~/ui-rtk/hooks/drawer"
import type { AdCommentsDrawerProps } from "~/ui-rtk/components/ui/drawer/AdCommentsDrawer"
import { highlightRoasCell } from "~/ui-rtk/utils/table"
import {
  Legend,
  ScoreBadge,
  StanceBar,
  aspectLabel,
  platformLabel,
  stanceTitle,
} from "./reactions"
import useAdMetrics from "./useAdMetrics"

/** The element whose clicks must not close the drawer (row → row switches content). */
export const ADS_TABLE_ID = "comment-moderation-ads"
const CHART_ID = "comment-moderation-ads-table"

/** One table row: the reaction rollup plus the Ads tab numbers, flat for sorting. */
type AdRow = TMetric & {
  reaction: AdReaction
  ad_id: string
  ad_name: string
  campaign_name?: string
  adset_name?: string
  ad_status?: string
  score: number | null
  comments_judged: number
  hidden: number
  headline?: string
  spend?: number
  impressions?: number
  clicks?: number
  ctr?: number
  purchases?: number
  short_term_revenue_roas?: number
  brand_value_agg?: number
  brand_value_over_spend?: number
  long_term_revenue_roas?: number
}

/**
 * One row per ad that got comments in the window: how the audience reacted
 * (stance bar + score), how many comments and how many were hidden, the AI
 * headline when one exists, and the Paid Media Optimization Ads tab's
 * numbers for the same window. Rendered with the house TableVisualization
 * (sortable, formatted, ROAS-highlighted like the Ads tab); clicking a row
 * opens the ad's drawer with the full read and its comments.
 */
export default function AdsTable({
  data,
  isLoading,
  summaries,
  from,
  to,
  platform,
}: {
  data: AdReactionsReport | undefined
  isLoading: boolean
  summaries: AdCommentSummary[] | undefined
  from: string
  to?: string
  platform?: CommentPlatform
}) {
  const { openDrawer } = useDrawer()
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  // Cube ad metrics are an addition for real ads only; organic post rows
  // have no ad to look up and render fine without them.
  const adIds = useMemo(
    () => (data?.ads ?? []).filter(a => !a.organic).map(a => a.adId),
    [data],
  )
  const { metricsByAd, isLoading: metricsLoading } = useAdMetrics(
    adIds,
    from,
    to,
  )

  const rows = useMemo<AdRow[]>(() => {
    const summaryByAd = new Map((summaries ?? []).map(s => [s.adId, s]))
    return (data?.ads ?? []).map(ad => {
      const m = metricsByAd.get(ad.adId)
      const impressions = m?.impressions ?? 0
      return {
        reaction: ad,
        ad_id: ad.adId,
        ad_name: ad.organic
          ? `Organic post ${ad.postId ?? ""}`.trim()
          : (ad.adName ?? `Ad ${ad.adId}`),
        campaign_name: m?.campaign_name,
        adset_name: m?.adset_name,
        ad_status: m?.ad_status,
        score: ad.score,
        comments_judged: ad.comments,
        hidden: ad.hidden,
        headline: summaryByAd.get(ad.adId)?.headline,
        spend: m?.spend,
        impressions: m?.impressions,
        clicks: m?.clicks,
        ctr: m && impressions > 0 ? (m.clicks ?? 0) / impressions : undefined,
        purchases: m?.purchases,
        short_term_revenue_roas: m?.short_term_revenue_roas,
        brand_value_agg: m?.brand_value_agg,
        brand_value_over_spend: m?.brand_value_over_spend,
        long_term_revenue_roas: m?.long_term_revenue_roas,
      }
    })
  }, [data, summaries, metricsByAd])

  const open = (row: TMetric) => {
    const ad = (row as AdRow).reaction
    const props: AdCommentsDrawerProps = {
      adId: ad.adId,
      adName: ad.adName,
      organic: ad.organic,
      postId: ad.postId,
      platform: ad.platform,
      permalinkUrl: ad.permalinkUrl,
      from,
      to,
      filterPlatform: platform,
    }
    openDrawer({
      clickSourceId: ADS_TABLE_ID,
      type: DRAWER_TYPE.AdComments,
      props,
    } as TDrawerConfig)
  }

  const columnConfig = useMemo<TableVisualizationColumn[]>(
    () => [
      {
        key: "ad_name",
        header: "Ad",
        type: "title",
        width: 280,
        render: (row: TMetric) => {
          const r = row as AdRow
          const ad = r.reaction
          return (
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-system-gray-100">{r.ad_name}</span>
              <span className="truncate text-xs text-system-gray-500">
                {platformLabel(ad.platform)}
                {ad.aspects.length
                  ? ` · ${ad.aspects
                      .slice(0, 3)
                      .map(a => aspectLabel(a.aspect))
                      .join(", ")}`
                  : ""}
              </span>
              {r.campaign_name && (
                <span className="truncate text-xs text-system-gray-500">
                  {r.campaign_name}
                  {r.adset_name ? ` › ${r.adset_name}` : ""}
                  {r.ad_status ? ` · ${String(r.ad_status).toLowerCase()}` : ""}
                </span>
              )}
            </div>
          )
        },
      },
      {
        key: "score",
        header: "Reaction",
        type: "decimal",
        width: 220,
        render: (row: TMetric) => {
          const ad = (row as AdRow).reaction
          return (
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <StanceBar counts={ad} title={stanceTitle(ad)} />
              </div>
              <ScoreBadge score={ad.score} />
            </div>
          )
        },
      },
      {
        key: "comments_judged",
        header: "Comments",
        type: "number",
        width: 100,
      },
      {
        key: "hidden",
        header: "Hidden",
        type: "number",
        width: 90,
        render: (row: TMetric) => {
          const n = (row as AdRow).hidden
          return (
            <span className={n ? "text-rose-300" : "text-system-gray-500"}>
              {n}
            </span>
          )
        },
      },
      { key: "spend", header: "Spend", type: "currency", width: 100 },
      { key: "impressions", header: "Impressions", type: "number", width: 110 },
      { key: "clicks", header: "Clicks", type: "number", width: 90 },
      { key: "ctr", header: "CTR", type: "percent", width: 80 },
      {
        key: "purchases",
        header: "7d Click DR Revenue",
        type: "currency",
        width: 140,
      },
      {
        key: "short_term_revenue_roas",
        header: "7d Click DR ROAS",
        type: "coef",
        width: 130,
        highlightFn: highlightRoasCell,
      },
      {
        key: "brand_value_agg",
        header: "180d Brand Value",
        type: "currency",
        width: 130,
        isBrand: true,
      },
      {
        key: "brand_value_over_spend",
        header: "180d Brand ROAS",
        type: "coef",
        width: 130,
        highlightFn: highlightRoasCell,
      },
      {
        key: "long_term_revenue_roas",
        header: "Total ROAS",
        type: "coef",
        width: 110,
        highlightFn: highlightRoasCell,
      },
      {
        key: "headline",
        header: "What the audience says",
        type: "text",
        width: 320,
        disableSorting: true,
        render: (row: TMetric) => {
          const h = (row as AdRow).headline
          return h ? (
            <span className="line-clamp-2 text-sm text-system-gray-200">
              {h}
            </span>
          ) : (
            <span className="text-sm text-system-gray-600">—</span>
          )
        },
      },
    ],
    [],
  )

  let body: ReactNode
  if (isLoading && !data) {
    body = <p className="px-3 py-6 text-base text-system-gray-400">Loading…</p>
  } else if (!data || data.ads.length === 0) {
    body = (
      <p className="px-3 py-6 text-base text-system-gray-400">
        No ads with judged comments in this window. Turn on moderation under
        Settings above and run it once to populate the report.
      </p>
    )
  } else {
    body = (
      <TableVisualization
        metrics={rows as TMetric[]}
        columnConfig={columnConfig}
        paginationState={paginationState}
        setPaginationState={setPaginationState}
        enablePagination={false}
        chartId={CHART_ID}
        sortBy="comments_judged"
        sortDesc
        highlightCells
        onRowClick={open}
      />
    )
  }

  return (
    <section id={ADS_TABLE_ID} className="flex flex-col gap-system-xs">
      <div className="flex flex-wrap items-center justify-between gap-system-sm px-1">
        <div>
          <h2 className="text-base font-medium">Ads and how people reacted</h2>
          <p className="text-xs text-system-gray-500">
            Spend, revenue and ROAS are the Paid Media Optimization Ads tab
            numbers for the same window
            {metricsLoading ? " (loading…)" : ""}. Click a row for the full read
            and its comments.
          </p>
        </div>
        <Legend />
      </div>
      {body}
      {data && data.unscored > 0 && (
        <p className="px-1 text-xs text-system-gray-500">
          {data.unscored} comment{data.unscored === 1 ? "" : "s"} judged before
          reactions shipped are not counted here.
        </p>
      )}
    </section>
  )
}
